import { useNonce } from '#app/utils/nonce-provider.js';
import {forwardRef, ScriptHTMLAttributes, useState, useEffect} from 'react';

type ScriptProps = HydrogenScriptProps & ScriptAttributes;

interface HydrogenScriptProps {
  /** Wait to load the script until after the page hydrates. This prevents hydration errors for scripts that modify the DOM. Note: For security, `nonce` is not supported when using `waitForHydration`. Instead you need to add the domain of the script directly to your [Content Securitiy Policy directives](https://shopify.dev/docs/storefronts/headless/hydrogen/content-security-policy#step-3-customize-the-content-security-policy).*/
  waitForHydration?: boolean;
}

interface ScriptAttributes extends ScriptHTMLAttributes<HTMLScriptElement> {}

export const Script = forwardRef<HTMLScriptElement, ScriptProps>(
  (props, ref) => {
    const {waitForHydration, src, ...rest} = props;
    const nonce = useNonce();

    if (waitForHydration) return <LazyScript src={src} options={rest} />;

    return (
      <script
        suppressHydrationWarning
        {...rest}
        src={src}
        nonce={nonce}
        ref={ref}
      />
    );
  },
);

function LazyScript({
  src,
  options,
}: {
  src?: string;
  options: JSX.IntrinsicElements['script'];
}) {
  if (!src)
    throw new Error(
      '`waitForHydration` with the Script component requires a `src` prop',
    );

  useLoadScript(src, {
    attributes: options as Record<string, string>,
  });

  return null;
}

const SCRIPTS_LOADED: Record<string, Promise<boolean>> = {};

type LoadScriptOptions = {
  module?: boolean;
  in?: 'head' | 'body';
  attributes?: Record<string, string>;
};

export function loadScript(
  src: string,
  options?: LoadScriptOptions,
): Promise<boolean> {
  const isScriptLoaded = SCRIPTS_LOADED[src];

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  if (isScriptLoaded) {
    return isScriptLoaded;
  }

  const promise = new Promise<boolean>((resolve, reject) => {
    const script = document.createElement('script');
    if (options?.module) {
      script.type = 'module';
    } else {
      script.type = 'text/javascript';
    }
    script.src = src;
    script.onload = (): void => {
      resolve(true);
    };
    script.onerror = (): void => {
      reject(false);
    };
    if (options?.in === 'head') {
      document.head.appendChild(script);
    } else {
      document.body.appendChild(script);
    }

    const attributes = options?.attributes;
    if (attributes) {
      Object.keys(attributes).forEach((key) => {
        script.setAttribute(key, attributes[key] as string);
      });
    }
  });

  SCRIPTS_LOADED[src] = promise;

  return promise;
}

type LoadScriptParams = Parameters<typeof loadScript>;

/**
 * The `useLoadScript` hook loads an external script tag in the browser. It allows React components to lazy-load large third-party dependencies.
 */
export function useLoadScript(
  url: LoadScriptParams[0],
  options?: LoadScriptParams[1],
): ScriptState {
  const [status, setStatus] = useState<ScriptState>('loading');

  useEffect(
    () => {
      loadScript(url, options)
        .then(() => setStatus('done'))
        .catch(() => setStatus('error'));
    },
    // Ignore options changes since it won't trigger a new load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url],
  );

  return status;
}

type ScriptState = 'loading' | 'done' | 'error';